import { useQuery } from "@tanstack/react-query";
import { useSelectedPlanContext } from "../contexts/SelectedPlanContext";
import { usePageSettings } from "../contexts/PageSettingsContext";
import { useMemo } from "react";

export const useFetchPrice = (selectedService, paymentCycle = "monthly", count = 1) => {
	const { selectedLocation, currency } = usePageSettings();
	const { selectedPlan } = useSelectedPlanContext();

	// Memoize resource IDs for a stable query key
	const planDeps = useMemo(() => {
		if (!selectedPlan) return null;
		return {
			cpu: selectedPlan.cpu?.id,
			ram: selectedPlan.ram?.id,
			storage: selectedPlan.storage?.id,
			transfer: selectedPlan.transfer?.id,
			bandwidth: selectedPlan.bandwidth?.id,
			rack: selectedPlan.rack?.id,
			energy: selectedPlan.energy?.id,
			prefix: selectedPlan.prefix?.id,
			planId: selectedPlan.id,
			nas: selectedPlan.nas?.id,
		};
	}, [selectedPlan]);

	const fetchPrice = async () => {
		if (!selectedPlan || !selectedService || !selectedLocation.iata_code) return 0;

		let payloadData = {};

		switch (selectedService) {
			case "colocation":
				payloadData.resources = [
					{
						location_code: selectedLocation.iata_code,
						service: "colocation",
						id_rack: selectedPlan.rack?.id,
					},
					{
						location_code: selectedLocation.iata_code,
						service: "colocation",
						id_prefix: selectedPlan.prefix?.id,
					},
					{
						location_code: selectedLocation.iata_code,
						service: "colocation",
						id_energy: selectedPlan.energy?.id,
					},
					{
						location_code: selectedLocation.iata_code,
						service: "colocation",
						id_bandwidth: selectedPlan.bandwidth?.id,
					},
				];
				break;

			case "vps":
			case "baremetal":
				payloadData.resources = [
					{
						location_code: selectedLocation.iata_code,
						service: selectedService,
						id_cpu: selectedPlan.cpu?.id,
					},
					{
						location_code: selectedLocation.iata_code,
						service: selectedService,
						id_storage: selectedPlan.storage?.id,
					},
					{
						location_code: selectedLocation.iata_code,
						service: selectedService,
						id_ram: selectedPlan.ram?.id,
					},
					{
						location_code: selectedLocation.iata_code,
						service: selectedService,
						id_transfer: selectedPlan.transfer?.id,
					},
					{
						location_code: selectedLocation.iata_code,
						service: selectedService,
						id_bandwidth: selectedPlan.bandwidth?.id,
					},
				];
				break;

			case "storage":
				payloadData.resources = [
					{
						location_code: selectedLocation.iata_code,
						service: selectedService,
						id_nas: selectedPlan.nas?.id,
					},
				];
				break;

			case "ultraproxy":
				payloadData = { ultraproxies: [selectedLocation.iata_code] };
				break;

			case "bgp":
				payloadData = { bgps: [selectedLocation.iata_code] };
				break;

			case "iptransit":
				payloadData.resources = [
					{
						location_code: selectedLocation.iata_code,
						service: "iptransit",
						id_bandwidth: selectedPlan.bandwidth?.id,
					},
				];
				break;

			case "webhosting":
				payloadData.plans = [selectedPlan?.id];
				break;

			default:
				return 0;
		}

		const response = await fetch(
			`${process.env.GATSBY_ADDON_PRICES_ENDPOINT}?currency=${currency}&cycle=${paymentCycle}&count=${count}`,
			{
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(payloadData),
			}
		);

		if (!response.ok) return 0;

		const data = await response.json();
		return data?.price ?? 0;
	};

	// Determine if selectedPlan has all required fields
	const isEnabled = useMemo(() => {
		if (!selectedPlan || !selectedService || !selectedLocation?.iata_code) return false;

		switch (selectedService) {
			case "vps":
			case "baremetal":
				return !!(
					selectedPlan.cpu?.id &&
					selectedPlan.ram?.id &&
					selectedPlan.storage?.id &&
					selectedPlan.transfer?.id &&
					selectedPlan.bandwidth?.id
				);

			case "colocation":
				return !!(
					selectedPlan.rack?.id &&
					selectedPlan.prefix?.id &&
					selectedPlan.energy?.id &&
					selectedPlan.bandwidth?.id
				);

			case "storage":
				return !!selectedPlan.nas?.id;

			case "webhosting":
				return !!selectedPlan.id;

			case "ultraproxy":
			case "bgp":
				return true;

			case "iptransit":
				return !!selectedPlan.bandwidth?.id;

			default:
				return false;
		}
	}, [selectedPlan, selectedService, selectedLocation?.iata_code]);

	return useQuery({
		queryKey: ["price", selectedService, selectedLocation?.iata_code, currency, paymentCycle, count, planDeps],
		queryFn: fetchPrice,
		enabled: isEnabled,
		refetchOnWindowFocus: false,
	});
};
